import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { DebugPanelService } from '@finder/shared/services/debug-panel/debug-panel.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { ConfigService } from '@finder/core/config.service';
import { SearchSelection } from '@finder/features/search/interfaces/search-selection.interface';
import { TrimAllProvider, TRIM_ALL } from '@finder/shared/utils/string/trimAll';
import { WebSocketsService } from '@finder/shared/services/websockets/websockets.service';
import { TitleComponent } from '@finder/shared/components/title/title.component';
import { CommonModule } from '@angular/common';
import { InfiniteScrollingComponent } from '@finder/shared/components/infinite-scrolling/infinite-scrolling.component';
import { SearchCardComponent } from './components/search-card/search-card.component';
import { LoadingSpinnerComponent } from '@finder/shared/components/loading-spinner/loading-spinner.component';
import { NoResultsComponent } from './components/no-results/no-results.component';
import { DecodeURIPipe } from '@finder/shared/utils/pipes/decode-uri/decode-uri.pipe';

@Component({
    selector: 'finder-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    standalone: true,
    imports: [
        TitleComponent,
        CommonModule,
        InfiniteScrollingComponent,
        SearchCardComponent,
        LoadingSpinnerComponent,
        NoResultsComponent,
        DecodeURIPipe,
    ],
    providers: [
        FinderApiConnectorService,
        DebugPanelService,
        FinderAnalyticsService,
        LoggerService,
        WebSocketsService,
    ]
})
export class SearchComponent implements OnInit, AfterViewInit, OnDestroy {

    searchPage = 0;
    searchData: any;
    searchTerm: string;
    results: Array<any>;
    noResults: any;
    pageSize: number;
    searchSchedulesLabels: any;
    isSearchCompleted = false;
    isLoading = false;

    private destroy$ = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private finderApiConnectorService: FinderApiConnectorService,
        private debugPanelService: DebugPanelService,
        private viewContainerRef: ViewContainerRef,
        private finderAnalyticsService: FinderAnalyticsService,
        private configService: ConfigService,
        private logger: LoggerService,
        private webSocketService: WebSocketsService,
        @Inject(TRIM_ALL) private trimAll: TrimAllProvider,
    ) {}

    ngOnInit() {

        this.searchData = this.route.snapshot.data?.search;
        if (!this.searchData) {
            this.logger.error('Search SVC -- No service response');

            return;
        }

        this.searchTerm = decodeURIComponent(this.searchData.term);
        this.results = this.searchData.results;
        this.pageSize = this.searchData.pageSize;
        this.noResults = this.searchData.noResults;
        this.searchSchedulesLabels = this.searchData.schedulesLabels;

        // for best locale support, the search result summary contains two replacement tokens
        // (number of results and the serach term) placed appropriately for a given language
        this.searchData.totalResultsFoundLabel = this.searchData.totalResultsFoundLabel
            .replace('{NUM_OF_RESULTS}', this.searchData.totalNumberResults)
            .replace('{SEARCH_TERM}', this.searchTerm);

        // Sometimes the initial results are less than the page size. Meaning: the search is completed.
        this.checkCompletedSearch(this.results);
        this.finderAnalyticsService.initAnalyticsLibrary(this.searchData.analytics);
        this.finderAnalyticsService.getGuestModel()
            .pipe(takeUntil(this.destroy$))
            .subscribe(guestModel => {
                // Update the analytics model
                this.finderAnalyticsService.updateAnalyticsModel(
                    null,
                    guestModel,
                    '',
                    this.searchData.title,
                    '',
                    {
                        linkId:
                            `${
                                this.configService.getValue('siteId')
                            }_internalSearchKeywords_${
                                this.trimAll(this.searchTerm)
                            }_internalSearchNumResults_${
                                this.searchData.totalNumberResults
                            }`,
                        events: 'internalSearch',
                        internalSearchKeywords: this.searchTerm,
                        internalSearchNumResults: this.searchData.totalNumberResults
                    }
                );
            });

        this.startWebSocketConnection(this.searchData.updateItems, this.searchData.updatePermissions);
    }

    ngAfterViewInit() {
        this.debugPanelService.createDebugPanel(this.viewContainerRef, this.searchData);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();

        this.webSocketService.cleanUp();
    }

    checkCompletedSearch(results) {
        this.isSearchCompleted = (results.length + 1) <= this.pageSize;
    }

    onScroll(): void {
        this.searchPage++;
        this.isLoading = true;
        this.finderApiConnectorService.getSearchQuery(this.searchTerm, this.searchPage)
            .pipe(takeUntil(this.destroy$))
            .subscribe(search => {
                this.isLoading = false;
                this.results = this.results.concat(search.results);

                // Unsub if we have completed the search
                this.checkCompletedSearch(search.results);
                if (this.isSearchCompleted) {
                    this.destroy$.next();
                }
            });
    }

    onSearchSelection(options: SearchSelection): void {
        // before posting the selection, fire analytics to track user selection
        this.finderAnalyticsService.updateModelAndTrack(
            {
                linkId:
                    `${
                        this.configService.getValue('siteId')
                    }_internalSearchClickedUrl_${
                        options.pageUrl
                    }_listPosition_${
                        options.position
                    }`,
                events: 'internalSearchClick',
                internalSearchKeywords: this.searchTerm,
                internalSearchNumResults: this.searchData.totalNumberResults
            }
        );
        this.finderApiConnectorService.postSearchSelection(options);
    }

    startWebSocketConnection(facilityIds, updatePermissions) {
        // If there are no attractions or restaurants on the page there's nothing to listen for
        // If we don't have anything to watch for then stop as well
        if (!updatePermissions || !facilityIds) {
            return;
        }

        this.webSocketService.searchConnection(facilityIds, updatePermissions);
    }
}
